import { useMemo, useState } from 'react';
import { REVIEW_ROUNDS_BY_SIZE, REVIEW_ROUNDS_INPUT, reviewRoundsForSize, reviewRoundsText } from '@shared/factory-review';
import { modelUsesFast, runFastAvailable } from '@shared/chat-model';
import { claudeTrajectoryText, isNearLimit } from '@shared/machine-pick';
import { CONFLICT_FLOW_ID } from '@shared/types/tasks';
import type { FlowInputSpec, FlowSummary, RunDetail, RunMachineSuggestion, RunnableMachine, RunOpenPr, StartRunBody } from '@shared/types/factory';
import { AppAlert } from '@/components/AppAlert';
import { ExternalLink } from '@/components/ExternalLink';
import { FormField } from '@/components/FormField';
import { InfoTooltip } from '@/components/InfoTooltip';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import { formatPercent } from '@/lib/formatters';
import type { ApiResponse } from '@/lib/api-contract';
import { listRunnableMachines } from '@/lib/projects-api';
import { listFlows, startRun } from '@/lib/runs-api';
import { prLabel } from '@/components/tasks/task-types';
import { isAboveCeiling, T3_LOGIN_HINT, type ContinuableRun } from './run-status';
import { RUN_PAUSE_INPUT, type RunPause } from '@shared/run-pause';

/** A model input's value, as the flow's templates read it (`inputs.model.model`). */
export interface ModelChoice {
  instanceId: string;
  model: string;
  /** SF-T-194: T3's model options; `[{ id: 'fastMode', value: true }]` with Fast Mode on. */
  options?: { id: string; value: boolean }[];
}

type InputValue = string | boolean | ModelChoice;

const RUNTIME_INPUT = 'runtimeMode';
/** SF-T-209: the flows' Live switch, before SF-T-302 made it the Before the PR choice (RUN_PAUSE_INPUT). */
const LIVE_INPUT = 'live';
/** The flow the dialog starts on when there's no run to carry on. */
const DEFAULT_FLOW_ID = 'implement-task';
/** The flow it starts on when there is a run to carry on (Feedback only, same thread, is the other choice, T-50). */
const DEFAULT_CONTINUE_FLOW_ID = 'follow-up';

function isModelChoice(value: unknown): value is ModelChoice {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ModelChoice).instanceId === 'string' &&
    typeof (value as ModelChoice).model === 'string'
  );
}

/** A Claude weekly limit's percent used (0 to 100), as "94%". */
const weeklyText = (percent: number | null) => formatPercent(percent === null ? null : percent / 100, { maximumFractionDigits: 0 });

/** "every machine…" → "Every machine….". */
const sentence = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}${/[.!?]$/.test(text) ? '' : '.'}`;

const modelKey = (m: ModelChoice) => `${m.instanceId}::${m.model}`;

/** SF-T-194: the model with Fast Mode on or off; models that don't take it (anything but Claude Opus) have no options. */
function withFast(choice: ModelChoice, on: boolean): ModelChoice {
  const { instanceId, model } = choice;
  return on && runFastAvailable(instanceId, model) ? { instanceId, model, options: [{ id: 'fastMode', value: true }] } : { instanceId, model };
}

/** SF-T-194: why Fast Mode starts on or off for this machine: its Claude weekly limit's trajectory. */
function fastModeNote(machine: RunnableMachine): string {
  return `${claudeTrajectoryText(machine.name, machine.claude_weekly_projected)}, so it starts ${machine.claude_fast ? 'on' : 'off'}.`;
}

function parseModelKey(key: string): ModelChoice {
  const at = key.indexOf('::');
  return { instanceId: key.slice(0, at), model: key.slice(at + 2) };
}

/** Every model the machine offers, as choices: "Codex · GPT-6.1 Sol", or "Claude Opus 5.5" when the model already names its provider (SF-T-229). */
function machineModels(machine: RunnableMachine | undefined): { key: string; label: string }[] {
  return (machine?.providers ?? []).flatMap((p) =>
    p.models.map((m) => ({
      key: modelKey({ instanceId: p.instance_id, model: m.id }),
      label: m.label.toLowerCase().startsWith(p.display_name.toLowerCase()) ? m.label : `${p.display_name} · ${m.label}`,
    }))
  );
}

/** SF-T-229: the flow's one line under its picker: its summary, else its description's first sentence. */
function flowSummaryText(flow: Pick<FlowSummary, 'summary' | 'description'>): string {
  if (flow.summary) return flow.summary;
  const first = /^.+?[.!?](?=\s|$)/.exec(flow.description.trim());
  return first ? first[0] : flow.description;
}

/**
 * The starting value of each input: the flow's default where the machine can honour it. T-75: review
 * rounds that default to `by size` start on the number the task's size suggests, so it shows.
 */
function defaultInputs(
  flow: FlowSummary | undefined,
  machine: RunnableMachine | undefined,
  taskSize: string | null,
  continueLive = false,
  continuePause: RunPause | null = null
): Record<string, InputValue> {
  const out: Record<string, InputValue> = {};
  if (!flow) return out;
  for (const [name, spec] of Object.entries(flow.inputs)) {
    if (spec.type === 'model') {
      const models = machineModels(machine);
      const wanted = isModelChoice(spec.default) ? modelKey(spec.default) : null;
      const key = models.find((m) => m.key === wanted)?.key ?? models[0]?.key;
      // SF-T-194: Fast Mode starts on the machine's own decision, as the AI builder's runs there use it.
      if (key) out[name] = withFast(parseModelKey(key), Boolean(machine?.claude_fast));
    } else if (spec.type === 'enum') {
      const options = spec.options ?? [];
      const wanted = typeof spec.default === 'string' && options.includes(spec.default) ? spec.default : options[0];
      const suggested = String(reviewRoundsForSize(taskSize));
      // SF-T-302: a Follow up starts the way the run it carries on got to its PR (suggestions first, straight to PR, live).
      const carried = name === RUN_PAUSE_INPUT && flow.continues_run && continuePause && options.includes(continuePause) ? continuePause : null;
      if (carried) out[name] = carried;
      else if (name === REVIEW_ROUNDS_INPUT && wanted === REVIEW_ROUNDS_BY_SIZE && options.includes(suggested)) out[name] = suggested;
      else if (wanted) out[name] = wanted;
    } else if (spec.type === 'boolean') {
      // SF-T-209: a live run's Follow up starts live again.
      out[name] = name === LIVE_INPUT && flow.continues_run && continueLive ? true : spec.default === true;
    } else {
      out[name] = typeof spec.default === 'string' ? spec.default : '';
    }
  }
  return out;
}

/** A task as the dialog names it: "SF-T-43 Part A". */
export interface RunSubtaskChoice {
  id: string;
  ref: string;
  title: string;
}

/** The Start from choice for the default branch. */
const DEFAULT_BASE = 'default';

export interface RunTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  /** "SF-T-42", for the title. */
  taskRef: string;
  /** T-75: the task's size (XS to XL), for the review rounds it suggests. */
  taskSize?: string | null;
  /** Called with the new run; the caller usually goes to its page. */
  onStarted: (run: RunDetail) => void;
  /**
   * The task's run a follow-up would carry on (continuableRun), or null. Flows that carry on a run
   * (Follow up) are offered only with one, and are then the default, on that run's machine.
   */
  continueFrom?: ContinuableRun | null;
  /** The project (id or slug), when the dialog opens outside the project's own routes. */
  project?: string;
  /**
   * T-42: build these sub-tasks of the task together (one run, one thread, one branch and one PR).
   * Flows that carry on a run aren't offered.
   */
  subtasks?: RunSubtaskChoice[];
  /** T-42: tasks with an open PR the run may start from instead of the default branch (stacking). */
  stackChoices?: RunSubtaskChoice[];
  /** The project's default branch, for the Start from choice. */
  defaultBranch?: string;
  /** Preview only (the /dev/ui gallery): show these instead of loading them. */
  preview?: { flows: FlowSummary[]; machines: RunnableMachine[]; suggested?: RunMachineSuggestion | null; openPr?: RunOpenPr | null };
  /** Preview only: start with this instead of POST /api/runs. */
  start?: (body: StartRunBody) => Promise<ApiResponse<{ run: RunDetail }>>;
}

/**
 * SF-T-194: a field whose box may hold a long checkout path. FormField's grid column is `auto`, which
 * grows to the path's full width; this one shrinks to the dialog, so the box clips the path on a phone.
 */
const SHRINKABLE_FIELD = 'grid-cols-[minmax(0,1fr)]';

/**
 * Run a flow on a task: pick the flow, a machine with a confirmed checkout of the project, the
 * model (from that machine's providers) and the access level, then start the run. Maintainers only;
 * the server checks too. It opens on Follow up when the task has a run to carry on, else on Implement task.
 */
export function RunTaskDialog(props: RunTaskDialogProps) {
  const { open, onOpenChange, taskRef, subtasks } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" description="Start a flow on this task on a fleet machine.">
        <DialogHeader>
          <DialogTitle>{subtasks?.length ? `Build ${subtasks.map((s) => s.ref).join(', ')}` : `Run ${taskRef}`}</DialogTitle>
        </DialogHeader>
        {open ? <RunTaskForm {...props} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function RunTaskForm({ onOpenChange, taskId, taskRef, taskSize = null, continueFrom: continueFromProp, subtasks, stackChoices, defaultBranch, onStarted, project, preview, start }: RunTaskDialogProps) {
  // A batch of sub-tasks (T-42) starts afresh: carrying on is for the task's own thread.
  const continueFrom = subtasks?.length ? null : continueFromProp;
  const [base, setBase] = useState(DEFAULT_BASE);
  /** SF-T-229: the flow's whole description, under its one-line summary. */
  const [aboutOpen, setAboutOpen] = useState(false);
  const options = useLoader(
    async (signal) => {
      if (preview) {
        return { flows: preview.flows, machines: preview.machines, suggested: preview.suggested ?? null, openPr: preview.openPr ?? null, error: null as string | null };
      }
      const [flows, machines] = await Promise.all([
        listFlows({ signal, project }),
        listRunnableMachines({ signal, project }, taskId),
      ]);
      const failed = flows.error ? flows : machines.error ? machines : null;
      return {
        flows: flows.data?.flows ?? [],
        machines: machines.data?.machines ?? [],
        suggested: machines.data?.suggested ?? null,
        openPr: machines.data?.open_pr ?? null,
        error: failed ? describeApiError(failed) : null,
      };
    },
    [project, preview, taskId]
  );
  // SF-T-79: Fix conflicts starts from a PR's conflicts (the task's Fix conflicts), never from here.
  const flows = useMemo(() => (options.data?.flows ?? []).filter((f) => f.id !== CONFLICT_FLOW_ID), [options.data]);
  const machines = useMemo(() => options.data?.machines ?? [], [options.data]);

  const [flowId, setFlowId] = useState<string | null>(null);
  const [machineId, setMachineId] = useState<string | null>(null);
  // Edits on top of the defaults; cleared when the flow or machine changes.
  const [edits, setEdits] = useState<Record<string, InputValue>>({});
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The server's field errors: `machine_id`, `inputs.<name>`. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // A flow that carries on a run needs that run's machine to be runnable now.
  const continueMachine = continueFrom ? machines.find((m) => m.id === continueFrom.machineId) : undefined;
  const offered = continueMachine ? flows : flows.filter((f) => !f.continues_run);
  const defaultFlow =
    (continueMachine ? (offered.find((f) => f.id === DEFAULT_CONTINUE_FLOW_ID) ?? offered.find((f) => f.continues_run)) : undefined) ??
    offered.find((f) => f.id === DEFAULT_FLOW_ID) ??
    offered[0];
  const flow = offered.find((f) => f.id === flowId) ?? defaultFlow;
  const flowMachines = flow?.continues_run && continueMachine ? [continueMachine] : machines;
  // T-28: the machine the factory would use (the one that worked on the task, the creator's, the one with
  // most room), else (T-27) the first not updating its agent, set up for the project (SF-T-214) where one is.
  const suggested = options.data?.suggested ?? null;
  const suggestedMachine = suggested ? flowMachines.find((m) => m.id === suggested.machine_id && !m.updating) : undefined;
  const machine =
    flowMachines.find((m) => m.id === machineId) ??
    suggestedMachine ??
    flowMachines.find((m) => !m.updating && !m.not_ready) ??
    flowMachines.find((m) => !m.updating) ??
    flowMachines[0];
  const machineNote = machine && machine.id === suggestedMachine?.id ? suggested : null;
  // SF-T-155: a build with no thread to carry on takes the task's open PR over: same branch and PR, a new agent.
  const openPr = options.data?.openPr ?? null;
  const takesOver = flow?.takes_over_pr && !flow.continues_run ? openPr : null;
  // Start from only for a run that makes its own branch: a follow-up or a takeover starts from the PR's, so a
  // choice made under another flow isn't sent with it.
  const stackShown = Boolean(stackChoices?.length) && !flow?.continues_run && !takesOver;
  const continueLive = Boolean(continueFrom?.live);
  const continuePause = continueFrom?.pause ?? null;
  const inputs = useMemo(
    () => ({ ...defaultInputs(flow, machine, taskSize, continueLive, continuePause), ...edits }),
    [flow, machine, taskSize, continueLive, continuePause, edits]
  );
  const runtime = typeof inputs[RUNTIME_INPUT] === 'string' ? (inputs[RUNTIME_INPUT] as string) : null;
  const aboveCeiling = machine ? isAboveCeiling(runtime, machine.t3_runtime_ceiling) : false;

  if (options.loading && !options.data) return <LoadingState inline label="Loading flows and machines…" />;
  if (options.data?.error) {
    return <LoadError compact title="Couldn't load flows and machines" error={options.data.error} onRetry={options.reload} />;
  }

  const missing = !offered.length
    ? 'No flows are deployed.'
    : !machines.length
      ? 'No machine can run this project: it needs one online with a confirmed checkout of the repo (Fleet).'
      : null;

  async function submit() {
    if (!flow || !machine || starting) return;
    setStarting(true);
    setError(null);
    setFieldErrors({});
    const body: StartRunBody = {
      task_id: taskId,
      flow_id: flow.id,
      machine_id: machine.id,
      inputs,
      ...(subtasks?.length ? { subtask_ids: subtasks.map((s) => s.id) } : {}),
      ...(stackShown && base !== DEFAULT_BASE ? { stack_on_task_id: base } : {}),
    };
    const res = await (start ? start(body) : startRun(body, { project }));
    setStarting(false);
    if (!res.data) {
      setFieldErrors(res.fields ?? {});
      setError(startErrorText(res));
      // The flows or machines changed under the dialog: load them again.
      if (res.code === 'FLOW_NOT_FOUND' || res.code === 'MACHINE_NOT_RUNNABLE') options.reload();
      return;
    }
    onStarted(res.data.run);
    onOpenChange(false);
  }

  return (
    <>
      <form
        id="run-task-form"
        noValidate
        // min-w-0 and a minmax column: a long flow description or checkout path wraps or clips inside the
        // dialog instead of widening it past a phone's screen (SF-T-148).
        className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {missing ? <AppAlert tone="warning">{missing}</AppAlert> : null}
        {error ? <AppAlert tone="critical">{error}</AppAlert> : null}
        {subtasks?.length ? (
          <div className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              One agent builds these sub-tasks of {taskRef} together, in one branch and one PR, linked to each:
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              {subtasks.map((s) => (
                <li key={s.id}>
                  <span className="mr-1 tabular-nums text-muted-foreground">{s.ref}</span>
                  {s.title}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {stackShown ? (
          <FormField
            id="run-base"
            label="Start from"
            info="Stack on another sub-task's open PR: the run starts from its branch, and its PR goes against that branch"
            error={fieldErrors.stack_on_task_id}
          >
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger id="run-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT_BASE}>{defaultBranch ? `The default branch (${defaultBranch})` : 'The default branch'}</SelectItem>
                {(stackChoices ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    Stacked on {s.ref} {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        ) : null}
        {machine?.updating ? (
          <AppAlert tone="warning" compact>
            {machine.name} is updating its agent and takes new runs again once it restarts.
          </AppAlert>
        ) : null}
        {flow ? (
          <FormField label="Flow" className={SHRINKABLE_FIELD}>
            <Select
              value={flow.id}
              onValueChange={(id) => {
                setFlowId(id);
                setEdits({});
              }}
            >
              {/* The checkout path is cut off at the box's edge rather than widening the dialog (SF-T-148). */}
              <SelectTrigger className="w-full min-w-0 overflow-hidden *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:overflow-hidden">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {offered.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        ) : null}
        {flow ? (
          <div className="-mt-2 space-y-1 text-xs text-muted-foreground" data-testid="run-flow-about">
            {/* SF-T-229: one line, with the whole description behind More, so the form fits a phone. */}
            <div className="flex items-start justify-between gap-2">
              <p>{flowSummaryText(flow)}</p>
              {flowSummaryText(flow) !== flow.description ? (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  // A 44px tap target on a phone (the task pop-up is a phone page), without a taller line.
                  className="-my-1 h-6 shrink-0 px-1 text-muted-foreground hover:text-foreground max-md:-my-3 max-md:h-11"
                  aria-expanded={aboutOpen}
                  onClick={() => setAboutOpen((was) => !was)}
                >
                  {aboutOpen ? 'Less' : 'More'}
                </Button>
              ) : null}
            </div>
            {flowSummaryText(flow) === flow.description ? (
              // The whole description already shows: no More, so the flow's link sits under it.
              <ExternalLink href={flow.github_url} size="xs" variant="muted">
                View the flow on GitHub
              </ExternalLink>
            ) : aboutOpen ? (
              <div className="space-y-1 rounded-md bg-muted/50 p-2">
                <p>{flow.description}</p>
                <ExternalLink href={flow.github_url} size="xs" variant="muted">
                  View the flow on GitHub
                </ExternalLink>
              </div>
            ) : null}
            {flow.continues_run && continueFrom && machine ? (
              <p className="text-foreground">
                Carries on R-{continueFrom.number} on {machine.name}.
              </p>
            ) : null}
            {takesOver ? (
              <p className="text-foreground" data-testid="run-takes-over">
                Takes over R-{takesOver.run_number}’s open pull request{' '}
                <ExternalLink href={takesOver.pr_url} size="xs">
                  {prLabel(takesOver.pr_url)}
                </ExternalLink>{' '}
                on <span className="font-mono">{takesOver.branch}</span>: a new agent pushes to that PR. For a new branch, merge or close it first.
              </p>
            ) : null}
            {continueFrom && !continueMachine ? (
              <p>
                R-{continueFrom.number}’s machine can’t run this project now, so it can’t be followed up here.
              </p>
            ) : null}
          </div>
        ) : null}
        {machine ? (
          <FormField
            label="Machine"
            // SF-T-229: why the factory picked it, behind the icon rather than under the box.
            info={
              machineNote && flowMachines.length > 1
                ? `The factory’s pick: ${machineNote.why}. The coding agent works in the machine’s checkout of the project.`
                : 'The coding agent works in the machine’s checkout of the project.'
            }
            error={fieldErrors.machine_id}
            className={SHRINKABLE_FIELD}
          >
            <Select
              value={machine.id}
              onValueChange={(id) => {
                setMachineId(id);
                setEdits({});
                setFieldErrors({});
              }}
            >
              {/* SF-T-194: like the flow's, a long checkout path is cut off at the box's edge so the dialog fits a phone. */}
              <SelectTrigger className="w-full min-w-0 overflow-hidden *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:overflow-hidden">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {flowMachines.map((m) => (
                  <SelectItem key={m.id} value={m.id} disabled={m.updating}>
                    {m.name} <span className="font-mono text-2xs text-muted-foreground">{m.checkout_path}</span>
                    {m.updating ? <span className="text-2xs text-muted-foreground"> · updating its agent</span> : null}
                    {m.not_ready ? <span className="text-2xs text-warning-strong"> · not set up for this project</span> : null}
                    {isNearLimit(m) ? (
                      <span className="text-2xs text-warning-strong"> · Claude weekly {weeklyText(m.claude_weekly_percent)}</span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        ) : null}
        {machine?.not_ready ? (
          <AppAlert tone="warning" compact data-testid="run-machine-not-ready">
            {machine.name} is {machine.not_ready}. The run can still start (say, once that&rsquo;s fixed), but its setup will
            probably fail until then.
          </AppAlert>
        ) : null}
        {machine && isNearLimit(machine) ? (
          <AppAlert tone="warning" compact>
            {machineNote?.near_limit ? sentence(machineNote.near_limit) : `${machine.name} has used ${weeklyText(machine.claude_weekly_percent)} of its Claude weekly limit.`}{' '}
            The run can still start, and the task shows a Near limit warning while it goes.
          </AppAlert>
        ) : null}
        {flow && machine
          ? inputGroups(flow).map((group, i) => {
              const fields = group.map(([name, spec]) => (
                <FlowInputField
                  key={`${flow.id}:${machine.id}:${name}`}
                  name={name}
                  spec={spec}
                  value={inputs[name]}
                  machine={machine}
                  taskSize={taskSize}
                  error={fieldErrors[`inputs.${name}`]}
                  onChange={(value) => setEdits((prev) => ({ ...prev, [name]: value }))}
                />
              ));
              // SF-T-229: the model and the choices sit two to a row from sm up; stacked on a phone.
              return group[0][1].type === 'model' || group[0][1].type === 'enum' ? (
                <div key={i} className="grid grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2">
                  {fields}
                </div>
              ) : (
                fields
              );
            })
          : null}
        {aboveCeiling && machine ? (
          <AppAlert tone="warning" title={`${machine.name} allows up to ${machine.t3_runtime_ceiling ?? 'unknown'}`}>
            The run will be refused at this access level. {T3_LOGIN_HINT}
          </AppAlert>
        ) : null}
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="run-task-form"
          disabled={Boolean(missing) || aboveCeiling || Boolean(machine?.updating)}
          disabledReason={
            missing ??
            (aboveCeiling
              ? 'Choose an access level the machine allows'
              : machine?.updating
                ? `${machine.name} is updating its agent; it takes new runs again once it restarts`
                : undefined)
          }
          loading={starting}
          loadingText="Starting…"
        >
          Start run
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * SF-T-229: the flow's inputs in order, runs of model and enum inputs (boxes, two to a row on wider screens)
 * kept apart from the rest (switches and text, one to a row).
 */
function inputGroups(flow: FlowSummary): [string, FlowInputSpec][][] {
  const boxed = (spec: FlowInputSpec) => spec.type === 'model' || spec.type === 'enum';
  const groups: [string, FlowInputSpec][][] = [];
  for (const entry of Object.entries(flow.inputs)) {
    const last = groups[groups.length - 1];
    if (last && boxed(last[0][1]) && boxed(entry[1])) last.push(entry);
    else groups.push([entry]);
  }
  return groups;
}

/** Why POST /api/runs refused, in words for the dialog (field errors show on their fields too). */
function startErrorText(res: ApiResponse<unknown>): string {
  switch (res.code) {
    case 'TASK_NOT_FOUND':
      return 'This task is gone (deleted, or moved to another project). Close the dialog and reload the Board.';
    case 'MACHINE_NOT_RUNNABLE':
      return res.error ?? 'That machine can’t run this project now: it went offline, or its checkout isn’t confirmed. Choose another.';
    case 'FLOW_NOT_FOUND':
      return 'That flow isn’t deployed any more. Choose another.';
    case 'RUNTIME_ABOVE_CEILING':
      return res.error ?? `The access level is above what this machine's T3 token allows. ${T3_LOGIN_HINT}`;
    case 'INVALID_INPUT':
      return res.error ?? 'Some inputs need fixing.';
    case 'SUBTASK_NOT_FOUND':
      return res.error ?? 'One of the sub-tasks is gone or moved. Close the dialog and pick again.';
    case 'NOTHING_TO_STACK_ON':
      return res.error ?? 'That task has no open PR to stack on any more. Choose another starting point.';
    default:
      return describeApiError(res);
  }
}

/**
 * T-75: a review rounds choice in words: "By size (M: 1 round)", "No review, checks only", "2 rounds",
 * with "suggested for M" on the number the task's size calls for.
 */
function reviewRoundsOption(option: string, taskSize: string | null): string {
  const suggested = reviewRoundsForSize(taskSize);
  const sized = taskSize ?? 'no size';
  if (option === REVIEW_ROUNDS_BY_SIZE) return `By size (${sized}: ${reviewRoundsText(suggested).toLowerCase()})`;
  if (!/^\d+$/.test(option)) return option;
  const rounds = Number(option);
  const text = rounds === 0 ? 'No review, checks only' : reviewRoundsText(rounds);
  return rounds === suggested ? `${text} · suggested${taskSize ? ` for ${taskSize}` : ' with no size'}` : text;
}

function FlowInputField({
  name,
  spec,
  value,
  machine,
  taskSize,
  error,
  onChange,
}: {
  name: string;
  spec: FlowInputSpec;
  value: InputValue | undefined;
  machine: RunnableMachine;
  taskSize: string | null;
  /** The server's error for this input (`inputs.<name>`). */
  error?: string;
  onChange: (value: InputValue) => void;
}) {
  const id = `run-input-${name}`;
  if (spec.type === 'model') {
    const models = machineModels(machine);
    if (!models.length) {
      return <AppAlert tone="warning">{machine.name} reports no models. Check T3 Code on that machine.</AppAlert>;
    }
    const choice = isModelChoice(value) ? value : null;
    return (
      // One cell with its Fast Mode switch, so the pair stays together in the dialog's two-column row (SF-T-229).
      <div className="grid min-w-0 content-start gap-2">
        <FormField id={id} label={spec.label} info={spec.description} error={error} className={SHRINKABLE_FIELD}>
          {/* SF-T-194: a new model starts again on the machine's Fast Mode decision. */}
          <Select value={choice ? modelKey(choice) : undefined} onValueChange={(key) => onChange(withFast(parseModelKey(key), machine.claude_fast))}>
            <SelectTrigger id={id} className="w-full">
              <SelectValue placeholder="Choose a model" />
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.key} value={m.key}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        {choice && runFastAvailable(choice.instanceId, choice.model) ? (
          // The switch on the left, beside its label, as the app's other switches are; why it starts on or off is behind the icon (SF-T-229).
          <div className="flex items-center gap-2" data-testid="run-fast-mode">
            <Switch id={`${id}-fast`} checked={modelUsesFast(choice)} onCheckedChange={(on) => onChange(withFast(choice, on))} />
            <Label htmlFor={`${id}-fast`}>Fast Mode</Label>
            <InfoTooltip label="Fast Mode">Faster, but uses more of the weekly limit. {fastModeNote(machine)}</InfoTooltip>
          </div>
        ) : null}
      </div>
    );
  }
  if (spec.type === 'boolean') {
    // SF-T-209: an on/off input (Live), as a switch beside its label; what it does is behind the icon (SF-T-229).
    return (
      <div className="space-y-1" data-testid={`run-input-${name}`}>
        <div className="flex items-center gap-2">
          <Switch id={id} checked={value === true} onCheckedChange={(on) => onChange(on)} />
          <Label htmlFor={id}>{spec.label}</Label>
          {spec.description ? <InfoTooltip label={spec.label}>{spec.description}</InfoTooltip> : null}
        </div>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    );
  }
  if (spec.type === 'enum') {
    return (
      <FormField id={id} label={spec.label} info={spec.description} error={error} className={SHRINKABLE_FIELD}>
        <Select value={typeof value === 'string' ? value : undefined} onValueChange={onChange}>
          <SelectTrigger id={id} className="w-full">
            <SelectValue placeholder="Choose" />
          </SelectTrigger>
          <SelectContent>
            {(spec.options ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {name === REVIEW_ROUNDS_INPUT ? reviewRoundsOption(option, taskSize) : option}
                {name === RUNTIME_INPUT && isAboveCeiling(option, machine.t3_runtime_ceiling) ? ' (above this machine’s limit)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    );
  }
  return (
    <FormField id={id} label={spec.label} info={spec.description} error={error}>
      <Input id={id} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}
